import './B2BImageCard.css';

import React, { useEffect, useRef, useState } from 'react';
import { fullImagePath, generateClassName } from './helpers';
import { Box, Card } from '@mui/material';
import useB2BTranslation from './hooks/useB2BTranslation';
import B2BDialog from './B2BDialog';
import CustomButton from './CustomButton';
import { Delete, Edit } from '@mui/icons-material';

const B2BImageCard = ({ imagePath = null,
    onChange = null, showChangeButton = true, requestChangeConfirmation = true,
    onDelete = null, showDeleteButton = false, requestDeleteConfirmation = true,
    synchrousUpdate = true,     // synchrousUpdate will expect an image update by the parent component
    // otherwise, it will update the image with the new image not triggering the refresh
    className = null, ...params }) => {
    const { t } = useB2BTranslation('b2b-common');
    const [fullPath, setFullPath] = useState(imagePath ? fullImagePath(imagePath) : null);
    const [file, setFile] = useState(null);
    const [tempFile, setTempFile] = useState(null);
    const fileInputRef = useRef(null);
    const [showConfirmChangePicture, setShowConfirmChangePicture] = useState(false);
    const [showConfirmDeletePicture, setShowConfirmDeletePicture] = useState(false);
    const [refresh, setRefresh] = useState(true);
    const [cacheBuster, setCacheBuster] = useState(Date.now());

    useEffect(() => {
        if (imagePath) {
            setFullPath(fullImagePath(imagePath));
        }
    }, [imagePath]);

    useEffect(() => {
        if (refresh) {
            setCacheBuster(Date.now());
            setRefresh(false);
        }
    }, [refresh]);

    const handleConfirmImageChange = () => {
        setShowConfirmChangePicture(false);
        if (file && onChange) {
            onChange(file);
            if (synchrousUpdate) {
                setTimeout(() => {
                    setRefresh(true);
                }, 1000);
            } else {
                setTempFile(file);
                setFile(null);
            }
        }
    }

    const handleNotConfirmImageChange = () => {
        setShowConfirmChangePicture(false);
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setFile(file);
        if (requestChangeConfirmation) {
            setShowConfirmChangePicture(true);
        } else {
            if (file && onChange) {
                onChange(file);
            }
        }
    }

    const handleChangeButtonClick = () => {
        fileInputRef.current.click();
    }

    const handleConfirmImageRemoval = () => {
        setShowConfirmDeletePicture(false);
        if (onDelete) {
            onDelete();
            if (synchrousUpdate) {
                setTimeout(() => {
                    setRefresh(true);
                }, 1000);
            } else {
                setFullPath(null);
                setTempFile(null);
            }
        }
    }

    const handleNotConfirmImageRemoval = () => {
        setShowConfirmDeletePicture(false);
    }    
    
    const handleDeleteButtonClick = () => {
        if (requestDeleteConfirmation) {
            setShowConfirmDeletePicture(true);
        } else {
            if (onDelete) {
                onDelete();
            }
        }
    }

    return (
        <Card variant='outlined' className={generateClassName('b2b-image-card', '', className)}  {...params}>
            <input
                type='file'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageChange}
            />
            {(showChangeButton || showDeleteButton) && (
                <Box className={generateClassName('b2b-image-card', '-buttons', className)}>
                    {showChangeButton && (
                    <CustomButton icon={<Edit />}
                        onClick={handleChangeButtonClick}
                        className={'b2b-image-card-change-button'}
                        />
                    )}
                    {showDeleteButton && (
                        <CustomButton icon={<Delete />}
                            onClick={handleDeleteButtonClick}
                            className={'b2b-image-card-delete-button'}
                        />
                    )}
                </Box>
            )}
            {(fullPath || !synchrousUpdate) && (
                <Box className={generateClassName('b2b-image-card', '-preview', className)}>
                    {(fullPath && synchrousUpdate) ? (
                        <img src={`${fullPath}?t=${cacheBuster}`} alt='Image' />
                    ) : (tempFile && !synchrousUpdate) ? (
                        <img src={URL.createObjectURL(tempFile)} alt='Image' />
                    ) : (!tempFile && !synchrousUpdate && !fullPath) ? (
                        <Box>{t('No Image')}</Box>
                    ) : (!tempFile && !synchrousUpdate && fullPath) ? (
                        <img src={`${fullPath}?t=${cacheBuster}`} alt='Image' />
                    ) :
                        null}
                </Box>
            )}
            <B2BDialog
                open={showConfirmChangePicture}
                title={t('Image Change Request')}
                onClose={() => setShowConfirmChangePicture(false)}
                buttons={[
                    {
                        label: t('Confirm'),
                        onClick: handleConfirmImageChange,
                    },
                    {
                        label: t('Cancel'),
                        onClick: handleNotConfirmImageChange,
                    },
                ]}
            >
                <p>{t('Are you sure you want to change the image?')}</p>
            </B2BDialog>
            <B2BDialog
                open={showConfirmDeletePicture}
                title={t('Image Deletion Request')}
                onClose={() => setShowConfirmDeletePicture(false)}
                buttons={[
                    {
                        label: t('Confirm'),
                        onClick: handleConfirmImageRemoval,
                    },
                    {
                        label: t('Cancel'),
                        onClick: handleNotConfirmImageRemoval,
                    },
                ]}
            >
                <p>{t('Are you sure you want to delete the image?')}</p>
            </B2BDialog>
        </Card>
    );
}

export default B2BImageCard;